import React, { useEffect, useState } from "react";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import { MDBAccordion, MDBAccordionItem, MDBBadge, MDBBtn, MDBBtnGroup, MDBCard, MDBCardBody, MDBCardHeader, MDBCardTitle, MDBCheckbox, MDBCol, MDBIcon, MDBInput, MDBRadio, MDBRow, MDBSpinner, MDBTextArea } from "mdb-react-ui-kit";
import ApiService from "../../Core/Service/ApiService";
import { useNavigate, useParams } from "react-router-dom";
import { SketchPicker } from "react-color";
import DateTimePicker from "react-datetime-picker";
import { LondonDate, capitalizeFirstLetter, getImageUrl, validateForm } from "../../Utils/utils";
import { toast } from "react-toastify";
import LoadingIndicator from "../../Components/LoadingIndicator/LoadingIndicator";
import { CmsType, PriceList } from "../../Constants/constants";
import { useContext } from "react";
import CmsContext from "../../Context/CmsContext";
import PriceSelect from "../../Components/CMS/PriceSelect";
import Ribbon from "../../Components/CMS/Ribbon";
import CategorySelect from "../../Components/CMS/CategorySelect";
import BrandSelect from "../../Components/CMS/BrandSelect";
import BranchSelect from "../../Components/CMS/BranchSelect";
import BrandMultiSelect from "../../Components/CMS/BrandMultiSelect";
import CategoryMultiSelect from "../../Components/CMS/CategoryMultiSelect";
import Media from "../../Components/CMS/Media";

// Trolleys an event's products can be added into (matches the web's ti ids).
const TROLLEY_OPTIONS = [
    { value: 6, label: 'Seasonal Presell (6)' },
    { value: 9, label: 'Trade Show (9)' },
];

const CmsEdit = React.memo(props => {
    const navigate = useNavigate();
    const cmsContext = useContext(CmsContext);
    const { currMainMenu, currSubMenu } = cmsContext;
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const now = LondonDate(new Date());
    const params = useParams();
    const { type } = params;
    const [formData, setFormData] = useState({
        type: type,
        start_date: LondonDate(new Date(`${now.year()}-${now.month() + 1}-${now.date()} 00:00:00`)),
        end_date: LondonDate(new Date(`${now.year()}-${now.month() + 1}-${now.date()} 23:59:59`)),
        active: true,
        internal_name: '',
        promotion_id: '',
        page_pos: 1,
        branches: [],
        price_list: [],
        organization_id: '',
        template_id: '',
        ribbon: {
            content: '',
            bg_color: '#fff',
            txt_color: '#f00',
            align: 'left',
        },
        media_web: {
            type: 'image',
            upload_file: '',
            external_link: '',
            active_link: 'external'
        },
        media_mobile: {
            type: 'image',
            upload_file: '',
            external_link: '',
            active_link: 'external'
        },
        data: {}
    });
    let rules = {
        start_date: {
            required: 'date'
        },
        end_date: {
            required: 'date'
        },
        page_pos: {
            required: 'number'
        },
        organization_id: {
            required: 'number'
        },
        branches: {
            required: 'array'
        },
        price_list: {
            required: 'array'
        }
    }
    if (type === CmsType.Mega_Menu_Banner) {
        delete rules.page_pos;
        delete rules.branches;
        delete rules.price_list;
    }
    if (type === CmsType.Splash_Screen) {
        delete rules.page_pos;
    }
    const [inputErrors, setInputErrors] = useState({});
    const [organizations, setOrganizations] = useState([]);
    const [templateOptions, setTemplateOptions] = useState();
    const [presellPromotionIds, setPresellPromotionIds] = useState([]);
    const [pendingFiles, setPendingFiles] = useState({});
    const handleFileSelected = (id, file) => {
        setPendingFiles(prev => ({ ...prev, [id]: file }));
    };


    let hiddenFields = [];
    switch (type) {
        case CmsType.Top_Ribbon:
            hiddenFields = ['page_pos'];
            break;
        case CmsType.Home_Banner:
            break;
        case CmsType.Category_Carousel:
            break;
        case CmsType.Category_Banner:
            hiddenFields = ['page_pos', 'ribbon'];
            break;
        case CmsType.Mega_Menu_Banner:
            hiddenFields = ['page_pos', 'ribbon', 'branch', 'price_list', 'dwell_time', 'template'];
            break;
        case CmsType.Products_Carousel:
            hiddenFields = [];
            break;
        case CmsType.Brochure:
            break;
        case CmsType.Bottom_Banner:
            break;
        case CmsType.Brand:
            break;
        case CmsType.MultiBuy:
            hiddenFields = ['ribbon']; // Temporarily hide ribbon for multibuy as per discussion, can be removed in future when multibuy supports ribbon customisation
            break;
        case CmsType.Sponsor:
            hiddenFields = ['page_pos'];
            break;
        case CmsType.Presell_Section:
        case CmsType.TradeShow_Section:
            hiddenFields = ['template', 'dwell_time'];
            break;
        case CmsType.Splash_Screen:
            hiddenFields = ['page_pos', 'ribbon', 'template', 'dwell_time'];
            break;
    }

    useEffect(() => {
        cmsContext.setBreadcrumb([
            { title: currMainMenu?.label ?? ''},
            { title: currSubMenu?.label ?? ''},
            { title: capitalizeFirstLetter(params.action) },
        ])
    }, [currMainMenu, currSubMenu])

    const isFieldHidden = (fieldName) => {
        return hiddenFields && hiddenFields.indexOf(fieldName) != -1;
    }

    const loadData = async () => {
        setLoading(true);

        // To load organizations
        let response = await ApiService.get('/organizations/all');
        const organizations = response.data.organizations;
        if (organizations?.length > 0) {
            setOrganizations(organizations.map(org => ({
                value: org.id,
                label: org.name
            })));
        }

        // To load promotion ids for the event section's price band
        // (Presell = band 06, TradeShow = band 09).
        if (type === CmsType.Presell_Section || type === CmsType.TradeShow_Section) {
            const promoEndpoint = type === CmsType.TradeShow_Section ? '/event_promotion_ids' : '/presell_promotion_ids';
            response = await ApiService.get(promoEndpoint);
            setPresellPromotionIds((response.data.promotion_ids ?? []).map(id => ({
                value: id, label: id
            })));
        }

        // To load templates
        response = await ApiService.get('/templates/all');
        const ts = response.data.templates;
        ts.unshift({
            id: '', internal_name: '- None -'
        });
        setTemplateOptions(ts.map(t=>(
            {value: t.id, label: t.internal_name}
        )));

        if (params.action == 'edit' || params.action == 'copy' || params.action == 'view') {
            response = await ApiService.get(`/cms/${params.id}`);
            const row = response.data.row;
            delete row.id;          // To delete id field
            delete row.cms_id;

            let cms_data = {
                ...formData,
                ...row,
                branches: row.branches ? row.branches.split(',') : [],
                price_list: row.price_list ? row.price_list.split(',') : [],
                start_date: LondonDate(new Date(row.start_date)),
                end_date: LondonDate(new Date(row.end_date)),
                active: (row.active == "1") ? true : false,
                ribbon: row.ribbon ? JSON.parse(row.ribbon) : {},
                data: row.data ? JSON.parse(row.data) : {},
                media_web: row.media_web ? JSON.parse(row.media_web) : formData.media_web,
                media_mobile: row.media_mobile ? JSON.parse(row.media_mobile) : formData.media_mobile,
            }
            
            // Initialize media fields in data for multibuy if not present
            if (type === CmsType.MultiBuy) {
                if (!cms_data.data.media_web) {
                    cms_data.data.media_web = {
                        type: 'image',
                        upload_file: '',
                        external_link: '',
                        active_link: 'external'
                    };
                }
                if (!cms_data.data.media_mobile) {
                    cms_data.data.media_mobile = {
                        type: 'image',
                        upload_file: '',
                        external_link: '',
                        active_link: 'external'
                    };
                }
            }

            setFormData({ ...cms_data });
        } else {
            setFormData({
                ...formData,
                organization_id: organizations?.length > 0 ? organizations[0].id : ''
            })
        }

        setLoading(false);
    }

    useEffect(() => {
        loadData();
    }, [params.action]);

    useEffect(() => {
        const ret = validateForm(formData, rules);
        const errors = { ...ret.errors };
        
        // Custom validation for multibuy fields
        if (type === CmsType.MultiBuy) {
            if (!errors.data) {
                errors.data = {};
            }
            
            // Validate label
            if (!formData.data?.label || formData.data.label.trim() === '') {
                errors.data.label = 'Label is required.';
            }
            
            // Validate incentive_type
            if (!formData.data?.incentive_type) {
                errors.data.incentive_type = 'Incentive Type is required.';
            }
            
            // Validate trigger_qty
            const triggerQty = formData.data?.trigger_qty;
            if (triggerQty === undefined || triggerQty === null || triggerQty === '') {
                errors.data.trigger_qty = 'Trigger Qty is required.';
            } else {
                const triggerQtyNum = parseFloat(triggerQty);
                if (isNaN(triggerQtyNum)) {
                    errors.data.trigger_qty = 'Trigger Qty must be a valid number.';
                } else if (triggerQtyNum < 0) {
                    errors.data.trigger_qty = 'Trigger Qty must be greater than or equal to 0.';
                }
            }
            
            // Validate incentive_qty
            const incentiveQty = formData.data?.incentive_qty;
            if (incentiveQty === undefined || incentiveQty === null || incentiveQty === '') {
                errors.data.incentive_qty = 'Incentive Qty is required.';
            } else {
                const incentiveQtyNum = parseFloat(incentiveQty);
                if (isNaN(incentiveQtyNum)) {
                    errors.data.incentive_qty = 'Incentive Qty must be a valid number.';
                } else if (incentiveQtyNum < 0) {
                    errors.data.incentive_qty = 'Incentive Qty must be greater than or equal to 0.';
                }
            }
            
            // Validate how_much field (only if incentive_type is not Free)
            if (formData.data?.incentive_type && formData.data.incentive_type !== 'Incentive Item Free') {
                const howMuch = formData.data.how_much;
                const incentiveType = formData.data.incentive_type;
                
                if (!howMuch || howMuch === '') {
                    errors.data.how_much = 'How Much? is required.';
                } else {
                    const numValue = parseFloat(howMuch);
                    if (isNaN(numValue)) {
                        errors.data.how_much = 'How Much? must be a valid number.';
                    } else if (numValue < 0) {
                        errors.data.how_much = 'How Much? must be greater than or equal to 0.';
                    } else if (incentiveType === 'Incentive Item Discount By Percentage') {
                        if (numValue > 99.9) {
                            errors.data.how_much = 'How Much? must be between 0 and 99.9 for percentage.';
                        }
                    } else if (incentiveType === 'Incentive Item Fixed Price' || incentiveType === 'Incentive Item Discount By Amount') {
                        if (numValue > 9999.99) {
                            errors.data.how_much = 'How Much? must be between 0 and 9999.99 for fixed price or amount.';
                        }
                    }
                }
            }
        }
        
        setInputErrors(errors);
    }, [formData, type]);

    const handleInputChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        })
    }
    const handleSelectChange = (opt, target) => {
        setFormData({
            ...formData,
            [target]: opt.value,
        })
    }
    const handleDateChange = (d, target) => {
        setFormData({
            ...formData,
            [target]: d
        })
    }
    const handleCheckChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.checked
        })
    }
    const handleBranchChange = (branches) => {
        setFormData({
            ...formData,
            branches
        })
    }
    const handlePriceChange = (price_list) => {
        setFormData({
            ...formData,
            price_list
        })
    }

    const handleDataInputChange = (e) => {
        setFormData({
            ...formData,
            data: {
                ...formData.data,
                [e.target.name]: e.target.value
            }
        })
    }
    const handleDataSelectChange = (opt, target) => {
        setFormData({
            ...formData,
            data: {
                ...formData.data,
                [target]: opt.value
            }
        })
    }
    const handleDataCheckChange = (e) => {
        setFormData({
            ...formData,
            data: {
                ...formData.data,
                [e.target.name]: e.target.checked
            }
        })
    }
    
    // Handler for multibuy media updates
    const handleMultiBuyMediaUpdate = (mediaData) => {
        setFormData({
            ...formData,
            data: {
                ...formData.data,
                ...mediaData
            }
        })
    }
    const setRibbon = (ribbon) => {
        setFormData({
            ...formData,
            ribbon
        })
    }

    const handleSave = async () => {
        const ret = validateForm(formData, rules);
        const errors = { ...ret.errors };
        
        // Custom validation for multibuy fields
        if (type === CmsType.MultiBuy) {
            if (!errors.data) {
                errors.data = {};
            }
            
            // Validate label
            if (!formData.data?.label || formData.data.label.trim() === '') {
                errors.data.label = 'Label is required.';
            }
            
            // Validate incentive_type
            if (!formData.data?.incentive_type) {
                errors.data.incentive_type = 'Incentive Type is required.';
            }
            
            // Validate trigger_qty
            const triggerQty = formData.data?.trigger_qty;
            if (triggerQty === undefined || triggerQty === null || triggerQty === '') {
                errors.data.trigger_qty = 'Trigger Qty is required.';
            } else {
                const triggerQtyNum = parseFloat(triggerQty);
                if (isNaN(triggerQtyNum)) {
                    errors.data.trigger_qty = 'Trigger Qty must be a valid number.';
                } else if (triggerQtyNum < 0) {
                    errors.data.trigger_qty = 'Trigger Qty must be greater than or equal to 0.';
                }
            }
            
            // Validate incentive_qty
            const incentiveQty = formData.data?.incentive_qty;
            if (incentiveQty === undefined || incentiveQty === null || incentiveQty === '') {
                errors.data.incentive_qty = 'Incentive Qty is required.';
            } else {
                const incentiveQtyNum = parseFloat(incentiveQty);
                if (isNaN(incentiveQtyNum)) {
                    errors.data.incentive_qty = 'Incentive Qty must be a valid number.';
                } else if (incentiveQtyNum < 0) {
                    errors.data.incentive_qty = 'Incentive Qty must be greater than or equal to 0.';
                }
            }
            
            // Validate how_much field (only if incentive_type is not Free)
            if (formData.data?.incentive_type && formData.data.incentive_type !== 'Incentive Item Free') {
                const howMuch = formData.data.how_much;
                const incentiveType = formData.data.incentive_type;
                
                if (!howMuch || howMuch === '') {
                    errors.data.how_much = 'How Much? is required.';
                } else {
                    const numValue = parseFloat(howMuch);
                    if (isNaN(numValue)) {
                        errors.data.how_much = 'How Much? must be a valid number.';
                    } else if (numValue < 0) {
                        errors.data.how_much = 'How Much? must be greater than or equal to 0.';
                    } else if (incentiveType === 'Incentive Item Discount By Percentage') {
                        if (numValue > 99.9) {
                            errors.data.how_much = 'How Much? must be between 0 and 99.9 for percentage.';
                        }
                    } else if (incentiveType === 'Incentive Item Fixed Price' || incentiveType === 'Incentive Item Discount By Amount') {
                        if (numValue > 9999.99) {
                            errors.data.how_much = 'How Much? must be between 0 and 9999.99 for fixed price or amount.';
                        }
                    }
                }
            }
        }
        
        const hasErrors = !ret.result || (errors.data && Object.keys(errors.data).length > 0);
        
        if (!hasErrors) {
            setInputErrors({});
            setSaving(true);

            let workingFormData = formData;
            const pendingIds = Object.keys(pendingFiles);
            if (pendingIds.length > 0) {
                try {
                    const uploaded = {};
                    for (const id of pendingIds) {
                        const file = pendingFiles[id];
                        const uploadFormData = new FormData();
                        uploadFormData.append('file', file);
                        uploadFormData.append('folder', id === 'media_mobile' ? 'mobile' : 'web');
                        const response = await ApiService.post('/media/upload', uploadFormData, { 'Content-Type': 'multipart/form-data' });
                        uploaded[id] = response.data.file;
                    }

                    const isMultibuy = type === CmsType.MultiBuy;
                    if (isMultibuy) {
                        const newData = { ...workingFormData.data };
                        for (const id of pendingIds) {
                            newData[id] = { ...(newData[id] || {}), upload_file: uploaded[id] };
                        }
                        workingFormData = { ...workingFormData, data: newData };
                    } else {
                        workingFormData = { ...workingFormData };
                        for (const id of pendingIds) {
                            workingFormData[id] = { ...(workingFormData[id] || {}), upload_file: uploaded[id] };
                        }
                    }
                    setFormData(workingFormData);
                    setPendingFiles({});
                } catch (e) {
                    toast.error('Image upload failed.');
                    setSaving(false);
                    return;
                }
            }

            const data = {
                ...workingFormData,
                dwell_time: workingFormData.dwell_time ?? 3,
                branches: workingFormData.branches?.join(','),
                price_list: workingFormData.price_list?.join(','),
                ribbon: JSON.stringify({
                    bg_color: '#f00',
                    txt_color: '#fff',
                    align: 'left',
                    ...workingFormData.ribbon
                }),
                data: JSON.stringify(workingFormData.data),
                media_web: JSON.stringify(workingFormData.media_web),
                media_mobile: JSON.stringify(workingFormData.media_mobile),
            }

            if(params.action == 'edit') {
                await ApiService.put(`/cms/${params.id}`, data);
            } else {
                await ApiService.post('/cms', data);
            }

            goBack();
            setSaving(false);
        } else {
            setInputErrors(errors);
        }
    }
    const gotoItems = () => {
        navigate(`/cms_item/${params.id}/list`);
    }
    const goBack = () => {
        navigate(-1);
    }

    const renderCommon = () => {
        return (
        <MDBCard>
            <MDBCardHeader>
                <MDBCardTitle>Common</MDBCardTitle>
            </MDBCardHeader>
            <MDBCardBody>
                <div className="d-flex">
                    <div className="me-4" title="Start date when CMS content is displayed">
                        <label className="required">Start Date:</label>
                        <div>
                            <DateTimePicker
                                onChange={d => handleDateChange(d, 'start_date')}
                                value={formData.start_date}
                                format="y/MM/dd HH:mm:ss"
                                disableClock={true}
                            />
                        </div>
                        {inputErrors?.start_date && <div className="error">{inputErrors.start_date}</div>}
                    </div>
                    <div className="me-4" title="End date when CMS content is displayed">
                        <label className="required">End Date:</label>
                        <div>
                            <DateTimePicker
                                onChange={d => handleDateChange(d, 'end_date')}
                                value={formData.end_date}
                                format="y/MM/dd HH:mm:ss"
                                disableClock={true}
                            />
                        </div>
                        {inputErrors?.end_date && <div className="error">{inputErrors.end_date}</div>}
                    </div>

                    <div className="me-4" title="Organization">
                        <label className="required">Organization:</label>
                        <div style={{ width: 200 }}>
                            <Select
                                options={organizations}
                                value={organizations.find(opt => opt.value == formData.organization_id)}
                                onChange={opt => handleSelectChange(opt, 'organization_id')}
                            />
                        </div>
                        {inputErrors?.organization_id && <div className="error">{inputErrors.organization_id}</div>}
                    </div>

                    {!isFieldHidden('page_pos') && (
                        <div className="me-4" title="The position where the CMS content is displayed in the Homepage">
                            <label className="required">Page Position:</label>
                            <div style={{ width: 100 }}>
                                <MDBInput name="page_pos" value={formData.page_pos ?? 1} onChange={handleInputChange} type="number" className="text-center" />
                            </div>
                            {inputErrors?.page_pos && <div className="error">{inputErrors.page_pos}</div>}
                        </div>
                    )}

                    {!isFieldHidden('dwell_time') && (
                        <div className="me-4" title="The position where the CMS content is displayed in the Homepage">
                            <label>Dwell Time:</label>
                            <div style={{ width: 100 }}>
                                <MDBInput name="dwell_time" value={formData.dwell_time ?? 3} onChange={handleInputChange} type="number" className="text-center" />
                            </div>
                        </div>
                    )}

                    <div className="me-4">
                        <label>Active:</label>
                        <MDBCheckbox name="active" checked={formData.active} onChange={handleCheckChange} title='Activate/Deactivate the CMS content' />
                    </div>
                </div>

                <div className="d-flex mt-2">
                    <div className="me-4" title='Internal name for CMS content'>
                        <label>Internal Name:</label>
                        <MDBInput name="internal_name" value={formData.internal_name} onChange={handleInputChange} />
                    </div>

                    {(type === CmsType.Presell_Section || type === CmsType.TradeShow_Section) && (
                    <div className="me-4" title='Promotion ID linking to products' style={{minWidth: '200px'}}>
                        <label>Promotion ID:</label>
                        <CreatableSelect
                            options={presellPromotionIds}
                            value={formData.promotion_id ? {value: formData.promotion_id, label: formData.promotion_id} : null}
                            onChange={opt => setFormData({...formData, promotion_id: opt?.value ?? ''})}
                            isClearable
                            placeholder="Select or type Promotion ID"
                        />
                    </div>
                    )}

                    {!isFieldHidden('template') && (
                    <div className="me-4" title="Template">
                        <label>Template:</label>
                        <div style={{minWidth: 150}}>
                            <Select options={templateOptions}
                                value={templateOptions?.find(opt=>opt.value == formData.template_id)}
                                onChange={opt=>handleSelectChange(opt, 'template_id')}
                            />
                        </div>
                    </div>)}
                </div>

                {(type === CmsType.Presell_Section || type === CmsType.TradeShow_Section) && (
                <div className="d-flex mt-2 align-items-center">
                    <div className="me-4 d-flex align-items-center" title="Show a countdown to the section's start date">
                        <MDBCheckbox name="countdown_to_start" label="Countdown To Start" checked={!!formData.data?.countdown_to_start} onChange={handleDataCheckChange} />
                    </div>
                    <div className="me-4 d-flex align-items-center" title="Show a countdown to the section's end date">
                        <MDBCheckbox name="countdown_to_end" label="Countdown To End" checked={!!formData.data?.countdown_to_end} onChange={handleDataCheckChange} />
                    </div>
                    <div className="d-flex align-items-center" title="Which trolley this event's products are added into">
                        <label className="me-2 mb-0">Trolley:</label>
                        <div style={{minWidth: 210}}>
                            <Select
                                options={TROLLEY_OPTIONS}
                                value={TROLLEY_OPTIONS.find(o => o.value == (formData.data?.trolley_id ?? (type === CmsType.TradeShow_Section ? 9 : 6)))}
                                onChange={opt => handleDataSelectChange(opt, 'trolley_id')}
                            />
                        </div>
                    </div>
                </div>
                )}
            </MDBCardBody>
        </MDBCard>
        )
    }

    const renderBranchAndPriceList = () => {
        return (
        <MDBCard className="mt-4">
            <MDBCardHeader>
                <MDBCardTitle>Branch & Price List</MDBCardTitle>
            </MDBCardHeader>
            <MDBCardBody>
                <BranchSelect
                    className=""
                    initialSelectedBranches={formData.branches}
                    onChange={handleBranchChange}
                    required={true}
                    inputError={inputErrors?.branches}
                    readonly={params.action == 'view'}
                />

                <PriceSelect
                    className="mt-4"
                    initialSelectedPrices={formData.price_list}
                    onChange={handlePriceChange}
                    required={true}
                    inputError={inputErrors.price_list}
                    readonly={params.action == 'view'}
                />
            </MDBCardBody>
        </MDBCard>
        )
    }

    const renderCustomData = () => {
        const availabilityOptions = [
            { value: 'N', label: 'New Item' },
            { value: 'C', label: 'Coming Soon' },
            { value: 'L', label: 'Low Stock' },
            { value: 'O', label: 'Out of Stock' },
            { value: 'T', label: 'Best Seller' },
        ];

        const sortByOptions = [
            { value: '', label: 'Default' },
            { value: 'price_asc', label: 'Price Low → High' },
            { value: 'price_desc', label: 'Price High → Low' },
            { value: 'description_asc', label: 'Description A → Z' },
            { value: 'description_desc', label: 'Description Z → A' },
            { value: 'por_asc', label: 'POR Low → High' },
            { value: 'por_desc', label: 'POR High → Low' },
            { value: 'brand_asc', label: 'Brand A → Z' },
            { value: 'brand_desc', label: 'Brand Z → A' },
            { value: 'best_selling', label: 'Best Selling' },
        ];

        const priceListOptions = PriceList.map(p => ({ value: p.id, label: p.label }));

        const updateData = (updates) => {
            setFormData({ ...formData, data: { ...formData.data, ...updates } });
        };

        const selectionModeOptions = [
            { value: 'manual', label: 'Manual (Product Codes)' },
            { value: 'dynamic', label: 'Dynamic (Filters)' },
        ];

        const selectionMode = formData.data.selection_mode || 'manual';

        const renderProductsCarousel = () => (
            <MDBCard className="mt-4">
                <MDBCardBody>
                    <div className="mb-3">
                        <label>Selection Mode:</label>
                        <Select
                            options={selectionModeOptions}
                            value={selectionModeOptions.find(o => o.value === selectionMode)}
                            onChange={(opt) => updateData({ selection_mode: opt.value })}
                        />
                    </div>

                    {selectionMode === 'manual' && (
                        <div className="mb-3">
                            <label className="required">Product Codes (comma-separated):</label>
                            <MDBInput name='prod_codes' value={formData.data.prod_codes ?? ''} onChange={handleDataInputChange} />
                        </div>
                    )}

                    {selectionMode === 'dynamic' && (<>
                        <MDBAccordion alwaysOpen>
                            <MDBAccordionItem collapseId={1} headerTitle={<>Brands {(formData.data.brands?.length > 0) && <MDBBadge className="ms-2">{formData.data.brands.length}</MDBBadge>}</>}>
                                <BrandMultiSelect
                                    selected={formData.data.brands || []}
                                    onChange={(opts) => updateData({ brands: opts ? opts.map(o => o.value) : [] })}
                                />
                            </MDBAccordionItem>

                            <MDBAccordionItem collapseId={2} headerTitle={<>Categories {(formData.data.categories?.length > 0) && <MDBBadge className="ms-2">{formData.data.categories.length}</MDBBadge>}</>}>
                                <CategoryMultiSelect
                                    selected={formData.data.categories || []}
                                    onChange={(opts) => updateData({ categories: opts ? opts.map(o => o.value) : [] })}
                                />
                            </MDBAccordionItem>

                            <MDBAccordionItem collapseId={3} headerTitle={<>Price Lists {(formData.data.filter_price_lists?.length > 0) && <MDBBadge className="ms-2">{formData.data.filter_price_lists.length}</MDBBadge>}</>}>
                                <Select
                                    isMulti
                                    options={priceListOptions}
                                    value={priceListOptions.filter(o => (formData.data.filter_price_lists || []).includes(o.value))}
                                    onChange={(opts) => updateData({ filter_price_lists: opts ? opts.map(o => o.value) : [] })}
                                />
                            </MDBAccordionItem>

                            <MDBAccordionItem collapseId={4} headerTitle={<>Promotion ID {formData.data.promotion_id && <MDBBadge className="ms-2">1</MDBBadge>}</>}>
                                <MDBInput name='promotion_id' value={formData.data.promotion_id ?? ''} onChange={handleDataInputChange} />
                            </MDBAccordionItem>

                            <MDBAccordionItem collapseId={5} headerTitle={<>Availability {(formData.data.availability?.length > 0) && <MDBBadge className="ms-2">{formData.data.availability.length}</MDBBadge>}</>}>
                                <Select
                                    isMulti
                                    options={availabilityOptions}
                                    value={availabilityOptions.filter(o => (formData.data.availability || []).includes(o.value))}
                                    onChange={(opts) => updateData({ availability: opts ? opts.map(o => o.value) : [] })}
                                />
                            </MDBAccordionItem>
                        </MDBAccordion>

                        <hr />
                        <MDBRow>
                            <MDBCol md="3">
                                <MDBCheckbox
                                    label="Only products on promo"
                                    checked={formData.data.only_promo || false}
                                    onChange={(e) => updateData({ only_promo: e.target.checked })}
                                />
                            </MDBCol>
                            <MDBCol md="3">
                                <MDBCheckbox
                                    label="Only items (exclude cases)"
                                    checked={formData.data.only_items || false}
                                    onChange={(e) => updateData({ only_items: e.target.checked })}
                                />
                            </MDBCol>
                            <MDBCol md="3">
                                <label>Sort By:</label>
                                <Select
                                    options={sortByOptions}
                                    value={sortByOptions.find(o => o.value === (formData.data.sort_by || ''))}
                                    onChange={(opt) => updateData({ sort_by: opt?.value || '' })}
                                />
                            </MDBCol>
                            <MDBCol md="3">
                                <label>Max Products:</label>
                                <MDBInput type="number" name='max_products' value={formData.data.max_products ?? 20} onChange={handleDataInputChange} />
                            </MDBCol>
                        </MDBRow>
                    </>)}

                    <hr />
                    <h6 className="mb-3">Show All</h6>
                    <div>
                        <label>Show All Text:</label>
                        <MDBInput name='show_all_text' value={formData.data.show_all_text ?? ''} onChange={handleDataInputChange} placeholder="e.g. View All" />
                    </div>
                </MDBCardBody>
            </MDBCard>
        );

        const renderCategoryBanner = () => (
            <MDBCard className="mt-4">
                <MDBCardBody>
                    <CategorySelect allowAll data={{
                            top_cat_id: formData.data.top_cat_id,
                            sub_cat_id: formData.data.sub_cat_id
                        }}
                        setData={({top_cat_id, sub_cat_id}) => {
                            setFormData({
                                ...formData,
                                data: {
                                    ...formData.data,
                                    top_cat_id,
                                    sub_cat_id
                                }
                            })
                        }}
                    />
                </MDBCardBody>
            </MDBCard>
        )

        const renderMegaMenuBanner = () => (
            <MDBCard className="mt-4">
                <MDBCardBody>
                    <CategorySelect data={{
                            top_cat_id: formData.data.top_cat_id,
                            sub_cat_id: 0
                        }}
                        setData={({top_cat_id}) => {
                            setFormData({
                                ...formData,
                                data: {
                                    ...formData.data,
                                    top_cat_id,
                                    sub_cat_id: 0
                                }
                            })
                        }}
                        hideSubCategory
                    />
                </MDBCardBody>
            </MDBCard>
        )

        const renderMultiBuy = () => {
            const incentiveTypeOptions = [
                { value: 'Incentive Item Free', label: 'Incentive Item Free' },
                { value: 'Incentive Item Fixed Price', label: 'Incentive Item Fixed Price' },
                { value: 'Incentive Item Discount By Amount', label: 'Incentive Item Discount By Amount' },
                { value: 'Incentive Item Discount By Percentage', label: 'Incentive Item Discount By Percentage' }
            ];

            return (
                <MDBCard className="mt-4">
                    <MDBCardBody>
                        <div>
                            <label className="required">Label:</label>
                            <div style={{width: 525}}>
                                <MDBInput name='label' value={formData.data.label ?? ''} onChange={handleDataInputChange} />
                            </div>
                            {inputErrors?.data?.label && <div className="error">{inputErrors.data.label}</div>}
                        </div>

                        <div className="mt-4 d-flex">
                            <div className="">
                                <label className="required">Trigger Qty:</label>
                                <MDBInput type="number" name='trigger_qty' value={formData.data.trigger_qty ?? 0} onChange={handleDataInputChange} />
                                {inputErrors?.data?.trigger_qty && <div className="error">{inputErrors.data.trigger_qty}</div>}
                            </div>
                            <div className="ms-4 flex-fluid">
                                <label className="required">Trigger Items:</label>
                                <MDBInput name='trigger_items' value={formData.data.trigger_items ?? ''} onChange={handleDataInputChange} />
                            </div>
                        </div>

                        <div className="mt-4 d-flex">
                            <div className="">
                                <label className="required">Incentive Type:</label>
                                <div style={{width: 300}}>
                                    <Select
                                        options={incentiveTypeOptions}
                                        value={incentiveTypeOptions.find(opt => opt.value === formData.data.incentive_type)}
                                        onChange={opt => handleDataSelectChange(opt, 'incentive_type')}
                                    />
                                </div>
                                {inputErrors?.data?.incentive_type && <div className="error">{inputErrors.data.incentive_type}</div>}
                            </div>
                            {formData.data.incentive_type && formData.data.incentive_type !== 'Incentive Item Free' && (
                                <div className="ms-4">
                                    <label className="required">How Much?:</label>
                                    <div style={{width: 200}}>
                                        <MDBInput 
                                            type="number" 
                                            name='how_much' 
                                            value={formData.data.how_much ?? ''} 
                                            onChange={handleDataInputChange}
                                            step={formData.data.incentive_type === 'Incentive Item Discount By Percentage' ? '0.1' : '0.01'}
                                            min="0"
                                            max={formData.data.incentive_type === 'Incentive Item Discount By Percentage' ? '99.9' : '9999.99'}
                                        />
                                    </div>
                                    {inputErrors?.data?.how_much && <div className="error">{inputErrors.data.how_much}</div>}
                                </div>
                            )}
                        </div>

                        <div className="mt-4 d-flex">
                            <div className="">
                                <label className="required">Incentive Qty:</label>
                                <MDBInput type="number" name='incentive_qty' value={formData.data.incentive_qty ?? 0} onChange={handleDataInputChange} />
                                {inputErrors?.data?.incentive_qty && <div className="error">{inputErrors.data.incentive_qty}</div>}
                            </div>
                            <div className="flex-fluid ms-4">
                                <label className="required">Incentive Items:</label>
                                <MDBInput name='incentive_items' value={formData.data.incentive_items ?? ''} onChange={handleDataInputChange} />
                            </div>
                        </div>
                    </MDBCardBody>
                </MDBCard>
            )
        }
        
        const renderMultiBuyMedia = () => {
            // Initialize media fields if they don't exist
            const currentData = formData.data || {};
            if (!currentData.media_web) {
                currentData.media_web = {
                    type: 'image',
                    upload_file: '',
                    external_link: '',
                    active_link: 'external'
                };
            }
            if (!currentData.media_mobile) {
                currentData.media_mobile = {
                    type: 'image',
                    upload_file: '',
                    external_link: '',
                    active_link: 'external'
                };
            }
            
            // Create a wrapper formData for Media component that uses data.media_web and data.media_mobile
            const mediaFormData = {
                ...formData,
                media_web: currentData.media_web,
                media_mobile: currentData.media_mobile
            };
            
            // Create a custom setFormData that intercepts updates to media_web/media_mobile
            // and redirects them to formData.data.media_web and formData.data.media_mobile
            const handleMediaFormDataChange = (updatedFormData) => {
                const { media_web, media_mobile } = updatedFormData;

                setFormData(prev => {
                    const newData = { ...prev.data };
                    if (media_web) {
                        newData.media_web = media_web;
                    }
                    if (media_mobile) {
                        newData.media_mobile = media_mobile;
                    }
                    return {
                        ...prev,
                        data: newData
                    };
                });
            };
            
            return (
                <Media
                    formData={mediaFormData}
                    setFormData={handleMediaFormDataChange}
                    webField="media_web"
                    mobileField="media_mobile"
                    label="Offer Media"
                    onFileSelected={handleFileSelected}
                />
            );
        };

        const renderSponsor = () => (
            <MDBCard className="mt-4">
                <MDBCardBody>
                    <div>
                        <label className="required">Terms (you can input multiple terms with separating by comma):</label>
                        <MDBInput name='terms' value={formData.data?.terms ?? ''} onChange={handleDataInputChange} />
                    </div>
                    <div className="mt-3">
                        <label className="required">Product Codes:</label>
                        <MDBInput name='prod_codes' value={formData.data.prod_codes ?? ''} onChange={handleDataInputChange} />
                    </div>
                </MDBCardBody>
            </MDBCard>
        )

        const renderSplashScreen = () => (
            <MDBCard className="mt-4">
                <MDBCardBody>
                    <div>
                        <label className="required">Product Codes (comma-separated):</label>
                        <MDBInput name='prod_codes' value={formData.data.prod_codes ?? ''} onChange={handleDataInputChange} />
                    </div>
                    <div className="mt-3">
                        <label>Link URL:</label>
                        <MDBInput name='link' value={formData.data.link ?? ''} onChange={handleDataInputChange} placeholder="https://..." />
                    </div>
                </MDBCardBody>
            </MDBCard>
        )

        switch(type) {
            case CmsType.Products_Carousel:
                return (
                    <>
                        {renderProductsCarousel()}
                        <Media
                            formData={formData}
                            setFormData={setFormData}
                            webField="media_web"
                            mobileField="media_mobile"
                            label="Page Banner"
                            onFileSelected={handleFileSelected}
                        />
                    </>
                );
            case CmsType.Category_Banner:
                return renderCategoryBanner();
            case CmsType.Mega_Menu_Banner:
                return (
                    <>
                        {renderMegaMenuBanner()}
                        <Media
                            formData={formData}
                            setFormData={setFormData}
                            webField="media_web"
                            mobileField="media_mobile"
                            label="Banner"
                            onFileSelected={handleFileSelected}
                        />
                    </>
                );
            case CmsType.MultiBuy:
                return (
                    <>
                        {renderMultiBuy()}
                        {renderMultiBuyMedia()}
                    </>
                );
            case CmsType.Sponsor:
                return renderSponsor();
            case CmsType.Presell_Section:
            case CmsType.TradeShow_Section:
                return (
                    <Media
                        formData={formData}
                        setFormData={setFormData}
                        webField="media_web"
                        mobileField="media_mobile"
                        label="Banner"
                        onFileSelected={handleFileSelected}
                    />
                );
            case CmsType.Splash_Screen:
                return (
                    <>
                        {renderSplashScreen()}
                        <Media
                            formData={formData}
                            setFormData={setFormData}
                            webField="media_web"
                            mobileField="media_mobile"
                            label="Splash Media"
                            onFileSelected={handleFileSelected}
                        />
                    </>
                );
        }
    }

    const renderEdit = () => (
        <div className="py-4">
            { renderCommon() }

            { !isFieldHidden('branch') && renderBranchAndPriceList() }

            { !isFieldHidden('ribbon') && (
                <Ribbon ribbon={formData.ribbon} setRibbon={setRibbon} />
            ) }

            { renderCustomData() }
        </div>
    )

    return (<section className={`px-4 pb-4`}>
        <div className={`${params.action == 'view' ? 'disabled-opacity-1' : ''}`}>
            {renderEdit()}
        </div>

        <div className="d-flex justify-content-end mt-4">
            <MDBBtn color="default" onClick={goBack}>Back</MDBBtn>
            { params.id && ![CmsType.Products_Carousel, CmsType.Sponsor, CmsType.MultiBuy, CmsType.Presell_Section, CmsType.TradeShow_Section, CmsType.Mega_Menu_Banner, CmsType.Splash_Screen].includes(type) && (
                <MDBBtn className="ms-4" color="default" onClick={gotoItems}>Items</MDBBtn> 
            )}
            {params.action != 'view' && (
                <MDBBtn className="ms-4 united-cms-primary-btn" onClick={handleSave}>
                    {saving && <MDBSpinner size="sm" />}
                    <span className="ms-1">Save</span>
                </MDBBtn>
            )}
        </div>

        <LoadingIndicator show={loading} />
    </section>)
})

export default CmsEdit;